/**
 * Bridge from the notification WebSocket (`SocketProvider`) to the AI chat: when a `job.status`
 * notification arrives (a cloud job reached a terminal state), the socket dispatches it and the chat
 * matches the `run_id` to a pending run and auto-continues. A window CustomEvent keeps the two
 * (separate) sockets/providers decoupled.
 */

export const JOB_STATUS_EVENT = 'omics:job-status'

export type JobStatus = 'complete' | 'failed' | 'canceled'

export interface JobStatusDetail {
  runId: string
  status: JobStatus
  tool?: string
}

/** Called by SocketProvider on a `{type:'job.status'}` notification. */
export function dispatchJobStatus(detail: JobStatusDetail): void {
  if (typeof window === 'undefined' || !detail?.runId) return
  window.dispatchEvent(new CustomEvent<JobStatusDetail>(JOB_STATUS_EVENT, {detail}))
}

/** Subscribed by the chat wrapper. Returns an unsubscribe function. */
export function subscribeJobStatus(cb: (detail: JobStatusDetail) => void): () => void {
  const handler = (e: Event) => {
    const detail = (e as CustomEvent<JobStatusDetail>).detail
    if (detail?.runId) cb(detail)
  }
  window.addEventListener(JOB_STATUS_EVENT, handler)
  return () => window.removeEventListener(JOB_STATUS_EVENT, handler)
}

/** Normalize a raw notification-socket message into a JobStatusDetail, or null if not a job.status. */
export function parseJobStatusMessage(message: unknown): JobStatusDetail | null {
  if (!message || typeof message !== 'object') return null
  const m = message as Record<string, unknown>
  if (m.type !== 'job.status') return null
  const runId = String(m.run_id ?? m.runId ?? '').trim()
  if (!runId) return null
  const rawStatus = String(m.status ?? 'complete').toLowerCase()
  const status: JobStatus = rawStatus === 'failed' || rawStatus === 'canceled' ? (rawStatus as JobStatus) : 'complete'
  const tool = typeof m.tool === 'string' ? m.tool : undefined
  return {runId, status, tool}
}
